import { TradingExecutionRoute } from '@ebalance/proto/bfftypes/v1/trading_execution_route_pb'
import type { Supply48DataSource } from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import { Supply48MarketKind } from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import type { TFunction } from 'i18next'

/**
 * market_kind → 列ヘッダラベルの i18n キー
 * (mock / design HTML / enum 値順、Design Note Decisions #6)。
 */
export function marketKindLabelKey(kind: Supply48MarketKind): string {
  switch (kind) {
    case Supply48MarketKind.SPOT:
      return 'page:supply48Data.market.spot'
    case Supply48MarketKind.INTRADAY:
      return 'page:supply48Data.market.intraday'
    default:
      return 'page:supply48Data.market.unspecified'
  }
}

/**
 * 市場ルート約定 (ベースロード市場 / 先渡し市場) の市場名 i18n キー。相対経路は undefined。
 *
 * 市場ルート約定は相手方が取引所を介した匿名で取引先マスタを参照せず
 * `trading_partner_name` が空で届くため、市場名を取引先名の代わりに出す
 * (AC-S4D-056、enechain/ebalance#6109)。
 *
 * 匿名の業務条件は「先物 (EEX/TOCOM/ICE) または 受渡区分 JEPX × 市場約定経路」の
 * 2 軸だが、ここを約定経路だけで判定してよいのは、ベースロード市場・先渡し市場の
 * 受渡区分が JEPX に限られるため (AC-DUB-058。取引所〈JEPX〉の代行入札を経由する
 * 市場制度で、JEPX 以外の受渡区分は業務上あり得ない)。48 点データの契約 source は
 * 受渡区分を運ばないので、この不変条件が崩れたら経路だけでは判定できなくなる。
 */
function marketRouteLabelKey(route: TradingExecutionRoute): string | undefined {
  switch (route) {
    case TradingExecutionRoute.BASELOAD_MARKET:
      return 'page:supply48Data.executionRoute.baseloadMarket'
    case TradingExecutionRoute.FORWARD_MARKET:
      return 'page:supply48Data.executionRoute.forwardMarket'
    default:
      return undefined
  }
}

export type SourceDisplayLabelOptions = {
  /**
   * 取引 source で公開取引 ID を「取引先名（取引ID）」形式で併記するか
   * (Supply48DataChart の凡例・見出しのみが持つ既存書式)。既定 false。
   * 市場 source には取引IDが無いため効果を持たない。
   */
  includeTradeId?: boolean
}

/**
 * 供給源 (Supply48DataSource) の表示名を導出する純関数。
 *
 * AC-S4D-004/040: グリッド列見出し・セル aria-label・入力拒否理由の項目名・
 * グラフ見出し/系列名など、画面内の複数箇所が同じ「source → 表示名」を必要と
 * する (旧実装は各所で `detail.case === 'contract'` を個別に分岐しており、
 * 市場 variant の対応漏れがグラフで顕在化した — 1 箇所に集約して再発を防ぐ)。
 *
 * 取引 source は取引先名 (oneof detail.contract)、市場 source は market_kind
 * ラベル (スポット / 時間前、oneof detail.market)。いずれでもない
 * (未対応 source type、または source 自体が undefined) 場合は空文字。
 * 市場ルート約定の契約 source だけは取引先名の代わりに市場名を出す (AC-S4D-056)。
 */
export function sourceDisplayLabel(
  source: Supply48DataSource | undefined,
  t: TFunction,
  options: SourceDisplayLabelOptions = {},
): string {
  if (source === undefined) return ''
  if (source.detail.case === 'contract') {
    const { tradingPartnerName, publicTradeId, executionRoute } =
      source.detail.value
    const marketRouteKey = marketRouteLabelKey(executionRoute)
    const name =
      marketRouteKey === undefined ? tradingPartnerName : t(marketRouteKey)
    return options.includeTradeId === true ? `${name} (${publicTradeId})` : name
  }
  if (source.detail.case === 'market') {
    return t(marketKindLabelKey(source.detail.value.marketKind))
  }
  return ''
}
